import Vue from 'vue'
import Vuex from 'vuex'
import { VUE_APP_WS_URL } from '../config.js'
import i18n from '@/common/lang';
import { login, loginByWallet } from '@/api/login.js'
import { getVisibleMenus } from '@/api/frontendMenu'
import { getUserInfo, updateUserLang } from "@/api/user";
import { listContents, getEnabledMarketTypes } from '@/api/market'
import { getToken, setToken, removeToken } from '@/utils/auth'
import { setDisplayTimezone } from '@/utils/timezone'
import { MessageBox } from 'element-ui'

Vue.use(Vuex)

function establishWebSocketConnection(store, commit, state, resolve, reject) {
  if (state.ws && (state.ws.readyState === WebSocket.OPEN || state.ws.readyState === WebSocket.CONNECTING)) {
    resolve("连接已存在");
    return;
  }
  if (state.ws && state.ws.readyState !== WebSocket.CLOSED) {
    state.ws.close();
  }
  const ws = new WebSocket(state.wsUrl);
  state.ws = ws;
  ws.onopen = () => {
    if (state.userInfo && state.userInfo.id) {
      const authMessage = { type: "USER_AUTH", userId: String(state.userInfo.id) };
      ws.send(JSON.stringify(authMessage));
    }
    resolve("连接成功");
  };
  ws.onmessage = e => {
    try {
      if (e.data.startsWith('{') || e.data.startsWith('[')) {
        const data = JSON.parse(e.data);
        if (data.type === 'AUTH_SUCCESS') { return; }
        if (data.type === 'FORCE_LOGOUT') {
          const loginIp = data.loginIp || 'Unknown';
          const msg = i18n.t('Your account has been logged in from another location. Please verify.')
              + '\n\n' + i18n.t('Login IP') + ': ' + loginIp
              + '\n\n' + i18n.t('If this was not you, please change your password immediately.');
          // 关闭 WebSocket 防止重连
          if (state.ws) { state.ws.close(1000); }
          MessageBox.alert(msg, i18n.t('Security Alert'), {
            confirmButtonText: i18n.t('OK'),
            type: 'warning',
            showClose: false,
            callback: () => {
              removeToken();
              store.dispatch('loginOut').then(() => {
                const rt = require('@/router').default;
                if (!rt.currentRoute.path.startsWith('/pass/')) {
                  rt.push('/pass/login').catch(err => {
                    if (err.name !== 'NavigationDuplicated') { console.error('Navigation error:', err); }
                  });
                }
              });
            }
          });
          return;
        }
        if (Array.isArray(data)) { commit('CHANGE_WS_MARLET', data); return; }
        if (data.type === 'REAL_TIME_PNL') {
          const pnlData = {};
          if (data.data) { Object.keys(data.data).forEach(orderId => { pnlData[orderId] = data.data[orderId]; }); }
          commit('UPDATE_REAL_TIME_PNL', pnlData);
        }
        if (data.type === 'BATCH_REAL_TIME_PNL') {
          const batchPnLData = {};
          if (data.data) {
            Object.keys(data.data).forEach(userId => {
              if (String(userId) === String(state.userInfo.id)) {
                const userPnLData = data.data[userId];
                Object.keys(userPnLData).forEach(orderId => { batchPnLData[orderId] = userPnLData[orderId]; });
              }
            });
          }
          if (Object.keys(batchPnLData).length > 0) { commit('UPDATE_REAL_TIME_PNL', batchPnLData); }
        }
        if (data.type === 'USER_BALANCE_UPDATE') {
          const balanceData = { [data.userId]: { baseBalance: data.baseBalance, floatingPnL: data.floatingPnL, totalAssets: data.totalAssets, loanAmount: data.loanAmount, bondAmount: data.bondAmount, profit: data.profit, timestamp: data.timestamp } };
          commit('UPDATE_USER_BALANCE', balanceData);
          if (String(data.userId) === String(state.userInfo.id)) {
            const updatedUserInfo = { ...state.userInfo, amount: data.baseBalance, floatingPnL: data.floatingPnL, totalAssets: data.totalAssets, loanAmount: data.loanAmount, bondAmount: data.bondAmount, profit: data.profit };
            commit('set_userInfo', updatedUserInfo);
          }
        }
        if (data.type === 'AI_PROFIT') {
          commit('AI_PROFIT_UPDATE', data);
          const amt = parseFloat(data.profitAmount || 0).toFixed(2);
          commit('ADD_NOTIFICATION', {
            type: 'ai_profit',
            titleKey: 'AI Profit Received',
            msgKey: 'AI Profit Message',
            amount: amt,
            time: Date.now()
          });
        }
        if (data.type === 'AI_ADMIN_DISABLED') {
          const disabled = data.aiAdminDisabled === 1;
          const hasNotify = !!(data.title || data.message);
          commit('SET_AI_NOTIFY_DATA', { title: data.title || '', message: data.message || '' });
          if (hasNotify) {
            commit('SET_AI_ADMIN_DISABLED', disabled);
            if (!disabled) {
              commit('SET_AI_RESTORED_NOTIFY', true);
            }
          } else {
            state.aiAdminDisabled = disabled;
          }
          commit('ADD_NOTIFICATION', {
            type: disabled ? 'ai_disabled' : 'ai_restored',
            titleKey: data.title || (disabled ? 'AI Computing Power Exhausted' : 'AI Restored'),
            msgKey: data.message || (disabled ? 'AI Exhausted Message' : 'AI Restored Message'),
            customContent: !!(data.title || data.message),
            time: Date.now()
          });
        }
        if (data.type === 'ENABLED_MARKET_TYPES_UPDATE') {
          if (Array.isArray(data.data)) {
            commit('SET_ENABLED_MARKET_TYPES', data.data);
          }
        }
        if (data.type === 'MARKET_PRODUCT_UPDATE') {
          commit('INCREMENT_MARKET_DATA_VERSION');
        }
        if (data.type === 'GLOBAL_LOAN_ENABLED_UPDATE') {
          commit('SET_GLOBAL_LOAN_ENABLED', data.data || '0');
        }
        if (data.type === 'GLOBAL_CONTRACTS_ENABLED_UPDATE') {
          commit('SET_GLOBAL_CONTRACTS_ENABLED', data.data || '0');
        }
        if (data.type === 'GLOBAL_LEVERAGE_ENABLED_UPDATE') {
          commit('SET_GLOBAL_LEVERAGE_ENABLED', data.data || '0');
        }
        if (data.type === 'SHOW_FLOATING_PNL_UPDATE') {
          commit('SET_SHOW_FLOATING_PNL', data.data || '0');
        }
        if (data.type === 'DISPLAY_TIMEZONE_UPDATE') {
          commit('SET_DISPLAY_TIMEZONE', data.data || 'Asia/Shanghai');
        }
        if (data.type === 'MOBILE_NAV_MODE_UPDATE') {
          commit('SET_MOBILE_NAV_MODE', data.data || 'tabbar');
        }
        if (data.type === 'VISIBLE_MENUS_UPDATE') {
          if (Array.isArray(data.data)) {
            commit('SET_VISIBLE_MENU_KEYS', data.data);
          }
        }
        if (data.type === 'OPTIONS_ORDER_RESULT') {
          commit('SET_OPTIONS_ORDER_RESULT', data.data);
        }
        if (data.type === 'BATCH_USER_BALANCE_UPDATE') {
          const currentUserId = String(state.userInfo.id);
          if (data.data && data.data[currentUserId]) {
            const userBalanceInfo = data.data[currentUserId];
            const balanceData = { [currentUserId]: { baseBalance: userBalanceInfo.baseBalance, floatingPnL: userBalanceInfo.floatingPnL, totalAssets: userBalanceInfo.totalAssets, loanAmount: userBalanceInfo.loanAmount, bondAmount: userBalanceInfo.bondAmount, profit: userBalanceInfo.profit, timestamp: userBalanceInfo.timestamp } };
            commit('UPDATE_USER_BALANCE', balanceData);
            const updatedUserInfo = { ...state.userInfo, amount: userBalanceInfo.baseBalance, floatingPnL: userBalanceInfo.floatingPnL, totalAssets: userBalanceInfo.totalAssets, loanAmount: userBalanceInfo.loanAmount, bondAmount: userBalanceInfo.bondAmount, profit: userBalanceInfo.profit };
            commit('set_userInfo', updatedUserInfo);
          }
        }
        if (data.type === 'RECHARGE_APPROVED') {
          if (data.newBalance) {
            const updatedUserInfo = { ...state.userInfo, amount: data.newBalance };
            commit('set_userInfo', updatedUserInfo);
          }
          commit('SET_RECHARGE_APPROVED_DATA', {
            cryptoType: data.cryptoType || 'USD',
            cryptoAmount: data.cryptoAmount || '0',
            usdAmount: data.usdAmount || '0',
            newBalance: data.newBalance || '0'
          });
          commit('ADD_NOTIFICATION', {
            type: 'recharge_approved',
            titleKey: 'Deposit Approved',
            msgKey: 'Deposit Approved Message',
            amount: parseFloat(data.usdAmount || 0).toFixed(2),
            time: Date.now()
          });
        }
        if (data.type === 'WITHDRAWAL_APPROVED') {
          store.dispatch('GET_USER_INFO').catch(() => {});
          const isUsdMode = parseInt(data.withdrawalMode) === 1;
          const displayAmount = isUsdMode ? parseFloat(data.usdAmount || 0).toFixed(2) : data.amount || '0';
          commit('SET_WITHDRAWAL_APPROVED_DATA', {
            amount: displayAmount,
            address: data.address || '',
            withdrawalType: data.withdrawalType || 0,
            withdrawalMode: parseInt(data.withdrawalMode) || 0,
            usdAmount: data.usdAmount || '0'
          });
          commit('ADD_NOTIFICATION', {
            type: 'withdrawal_approved',
            titleKey: 'Withdrawal Approved',
            msgKey: 'Withdrawal Approved Message',
            amount: isUsdMode ? parseFloat(data.usdAmount || 0).toFixed(2) : parseFloat(data.amount || 0).toFixed(2),
            time: Date.now()
          });
        }
        if (data.type === 'WITHDRAWAL_REJECTED') {
          store.dispatch('GET_USER_INFO').catch(() => {});
          const isUsdModeRej = parseInt(data.withdrawalMode) === 1;
          const displayAmountRej = isUsdModeRej ? parseFloat(data.usdAmount || 0).toFixed(2) : data.amount || '0';
          commit('SET_WITHDRAWAL_REJECTED_DATA', {
            amount: displayAmountRej,
            address: data.address || '',
            withdrawalType: data.withdrawalType || 0,
            withdrawalMode: parseInt(data.withdrawalMode) || 0,
            usdAmount: data.usdAmount || '0',
            refundAmount: data.refundAmount || '0',
            reason: data.reason || ''
          });
          commit('ADD_NOTIFICATION', {
            type: 'withdrawal_rejected',
            titleKey: 'Withdrawal Rejected',
            msgKey: 'Withdrawal Rejected Message',
            amount: isUsdModeRej ? parseFloat(data.usdAmount || 0).toFixed(2) : parseFloat(data.amount || 0).toFixed(2),
            time: Date.now()
          });
        }
        if (data.type === 'RECHARGE_REJECTED') {
          commit('SET_RECHARGE_REJECTED_DATA', {
            cryptoType: data.cryptoType || 'USD',
            cryptoAmount: data.cryptoAmount || '0',
            reason: data.reason || ''
          });
          commit('ADD_NOTIFICATION', {
            type: 'recharge_rejected',
            titleKey: 'Deposit Rejected',
            msgKey: 'Deposit Rejected Message',
            amount: parseFloat(data.cryptoAmount || 0).toFixed(2),
            time: Date.now()
          });
        }
      } else {
      }
    } catch (error) {
      console.error("WebSocket消息解析失败:", error, "原始消息:", e.data);
    }
  };
  ws.onclose = (event) => {
    if (event.code !== 1000 && state.isLogin) {
      setTimeout(() => {
        store.dispatch("GET_WS_MARKET").catch(err => { console.error("WebSocket重连失败:", err); });
      }, 5000);
    }
  };
  ws.onerror = (error) => {
    console.error("WebSocket连接出错:", error);
    reject("连接出错");
  };
}

const store = new Vuex.Store({
  state: {
    innerWidth: 0,
    wsUrl: VUE_APP_WS_URL + "/message",
    marketWsList: [],
    realTimePnLData: {}, // 存储实时盈亏数据
    userBalanceData: {}, // 存储用户实时余额数据
    aiProfitData: null, // 最新AI收益推送数据
    aiAdminDisabled: false, // AI是否被管理员禁用
    aiRestoredNotify: false, // AI恢复通知标志
    aiNotifyTitle: '', // 自定义通知标题
    aiNotifyMessage: '', // 自定义通知内容
    notifications: JSON.parse(localStorage.getItem('notifications') || '[]'), // 通知列表
    ws: null, // 存储WebSocket实例
    currentLanguage: localStorage.getItem('language') || 'en',
    //不显示底部导航的页面
    footerWhiteList: ["reset", "login", "register"],
    lginWhiteList: ["google", "landing"],
    visibleMenuKeys: false,
    globalLoanEnabled: '1',
    globalContractsEnabled: '1',
    globalLeverageEnabled: '1',
    showFloatingPnl: '1',
    appDownloadShow: '1',
    appDownloadIosUrl: '',
    appDownloadAndroidUrl: '',
    optionsOrderResult: null,
    rechargeApprovedData: null,
    rechargeRejectedData: null,
    withdrawalApprovedData: null,
    withdrawalRejectedData: null,
    custodyAccountEnabled: '1',
    custodyAccountPercentage: '10',
    visitorMode: null,
    landingPageEnabled: '0',
    landingPageStyle: '1',
    landingPageData: null,
    landingConfigLoaded: false,
    showingLandingPage: false,
    landingEntered: false,
    homeTemplate: localStorage.getItem('homeTemplate') || 'default',
    aiLevelDisplayMode: localStorage.getItem('aiLevelDisplayMode') || 'grid',
    isLogin: localStorage.getItem('isLogin') || false,
    isPc: true,
    userInfo: (() => {
      const userInfoStr = localStorage.getItem('userInfo');
      if (userInfoStr) {
        try {
          const userInfo = JSON.parse(userInfoStr);
          return userInfo;
        } catch (error) {
          console.error('解析userInfo失败:', error);
          return {};
        }
      }
      return {};
    })(),
    token: localStorage.getItem('token') || '',
    contentsList: [],
    langIndex: 0,
    langList: [
      {
        name: 'English',
        value: 'en'
      },
      {
        name: '简体中文',
        value: 'zh'
      },
      {
        name: '繁體中文',
        value: 'hk'
      },
      {
        name: '日本語',
        value: 'ja'
      },
      {
        name: '한국어',
        value: 'ko'
      },
      {
        name: 'Deutsch',
        value: 'de'
      },
      {
        name: 'français',
        value: 'fr'
      },
      {
        name: 'Italiano',
        value: 'it'
      },
      {
        name: 'Español',
        value: 'es'
      },
      {
        name: 'العربية',
        value: 'ar'
      },
      {
        name: 'ภาษาไทย',
        value: 'th'
      },
    ],
    areaCodes: ["+49", "+55", "+60", "+61", "+64", "+65", "+66", "+81", "+91", "+852", "+886"],
    virtualStatus: localStorage.getItem('virtualStatus') || 1,
    showBoard: sessionStorage.getItem('showBoard') || 1,
    // 🚀 新增：钱包绑定状态
    boundWalletAddress: localStorage.getItem('boundWalletAddress') || '',
    boundChainType: localStorage.getItem('boundChainType') || '',
    enabledMarketTypes: [1, 2, 3, 4, 5, 6],
    marketDataVersion: 0,
    lastMarketType: null,
    siteName: (() => { const n = localStorage.getItem('siteName'); if (n) document.title = n; return n || ''; })(),
    siteLogo: localStorage.getItem('siteLogo') != null ? localStorage.getItem('siteLogo') : '',
    siteLogoDark: localStorage.getItem('siteLogoDark') || '',
    themeColors: (() => {
      try {
        const cached = localStorage.getItem('cachedThemeColors');
        if (cached) {
          const colors = JSON.parse(cached);
          const root = document.documentElement;
          const cssVarMap = {
            theme_primary_color: '--color-primary',
            theme_primary_grad_start: '--color-primary-grad-start',
            theme_primary_grad_end: '--color-primary-grad-end',
            theme_bg_color: '--color-bg',
            theme_card_bg_color: '--color-card-bg',
            theme_nav_bg_color: '--color-nav-bg',
            theme_text_color: '--color-text',
            theme_text_secondary: '--color-text-secondary',
            theme_border_color: '--color-border',
            theme_up_color: '--color-up',
            theme_down_color: '--color-down',
            theme_success_color: '--color-success',
            theme_warning_color: '--color-warning',
            theme_danger_color: '--color-danger',
          };
          Object.keys(cssVarMap).forEach(key => {
            if (colors[key]) root.style.setProperty(cssVarMap[key], colors[key]);
          });
          const gs = colors.theme_primary_grad_start;
          const ge = colors.theme_primary_grad_end;
          if (gs && ge) root.style.setProperty('--color-primary-gradient', `linear-gradient(180deg, ${gs}, ${ge})`);
          return colors;
        }
      } catch(e) {}
      return {};
    })(),
    customerServiceEnabled: localStorage.getItem('customerServiceEnabled') || '0',
    customerServiceUrl: localStorage.getItem('customerServiceUrl') || '',
    customerServiceIcon: localStorage.getItem('customerServiceIcon') || '',
    customerServiceAgents: (() => { try { return JSON.parse(localStorage.getItem('customerServiceAgents') || '[]'); } catch(e) { return []; } })(),
    salesmartlyEnabled: localStorage.getItem('salesmartlyEnabled') || '0',
    salesmartlyScript: localStorage.getItem('salesmartlyScript') || '',
    mobileNavMode: localStorage.getItem('mobileNavMode') || 'tabbar',
    displayTimezone: localStorage.getItem('displayTimezone') || 'Asia/Shanghai',
    themeModeEnabled: false,
    themeLightColors: null,
    themeDarkColors: null,
    currentThemeMode: localStorage.getItem('themeMode') || 'dark',
  },
  getters: {
    marketWsList: state => state.marketWsList,
    realTimePnLData: state => state.realTimePnLData,
    userBalanceData: state => state.userBalanceData,
    getMarkTypeList: (state) => {
      const allTypes = [
        { label: i18n.t('Market.forex'), value: "1" },
        { label: i18n.t('Market.cypto'), value: "2" },
        { label: i18n.t('Market.stocks'), value: "3" },
        { label: i18n.t('Market.futures'), value: "4" },
        { label: i18n.t('Market.ETF'), value: "5" },
        { label: i18n.t('Market.fund'), value: "6" },
      ];
      return allTypes.filter(t => state.enabledMarketTypes.includes(Number(t.value)));
    },
    getContentList: state => state.contentsList,
    getUserInfo: state => state.userInfo,
    langList: state => state.langList,
    langIndex: state => state.langIndex,
    virtualStatus: state => state.virtualStatus,
    // 🚀 新增：钱包绑定状态getter
    getBoundWallet: state => ({
      address: state.boundWalletAddress,
      chainType: state.boundChainType
    }),
  },
  mutations: {
    SetInnerWidth(state, width) {
      state.innerWidth = width;
    },
    SET_IS_PC(state, isPc) {
      state.isPc = isPc;
    },
    SET_VISIBLE_MENU_KEYS(state, keys) {
      state.visibleMenuKeys = keys;
    },
    SET_GLOBAL_LOAN_ENABLED(state, val) {
      state.globalLoanEnabled = val;
    },
    SET_GLOBAL_CONTRACTS_ENABLED(state, val) {
      state.globalContractsEnabled = val;
    },
    SET_GLOBAL_LEVERAGE_ENABLED(state, val) {
      state.globalLeverageEnabled = val;
    },
    SET_SHOW_FLOATING_PNL(state, val) {
      state.showFloatingPnl = val;
    },
    SET_APP_DOWNLOAD_SHOW(state, val) {
      state.appDownloadShow = val;
    },
    SET_APP_DOWNLOAD_IOS_URL(state, val) {
      state.appDownloadIosUrl = val;
    },
    SET_APP_DOWNLOAD_ANDROID_URL(state, val) {
      state.appDownloadAndroidUrl = val;
    },
    SET_OPTIONS_ORDER_RESULT(state, val) {
      state.optionsOrderResult = val;
    },
    CLEAR_OPTIONS_ORDER_RESULT(state) {
      state.optionsOrderResult = null;
    },
    SET_RECHARGE_APPROVED_DATA(state, val) {
      state.rechargeApprovedData = val;
    },
    CLEAR_RECHARGE_APPROVED_DATA(state) {
      state.rechargeApprovedData = null;
    },
    SET_RECHARGE_REJECTED_DATA(state, val) {
      state.rechargeRejectedData = val;
    },
    CLEAR_RECHARGE_REJECTED_DATA(state) {
      state.rechargeRejectedData = null;
    },
    SET_WITHDRAWAL_APPROVED_DATA(state, val) {
      state.withdrawalApprovedData = val;
    },
    CLEAR_WITHDRAWAL_APPROVED_DATA(state) {
      state.withdrawalApprovedData = null;
    },
    SET_WITHDRAWAL_REJECTED_DATA(state, val) {
      state.withdrawalRejectedData = val;
    },
    CLEAR_WITHDRAWAL_REJECTED_DATA(state) {
      state.withdrawalRejectedData = null;
    },
    SET_CUSTODY_ACCOUNT_ENABLED(state, val) {
      state.custodyAccountEnabled = val;
    },
    SET_CUSTODY_ACCOUNT_PERCENTAGE(state, val) {
      state.custodyAccountPercentage = val;
    },
    SET_LANG(state, lang) {
      state.currentLanguage = lang;
      localStorage.setItem('language', lang);
      // 已登录时同步语言偏好到后端
      if (state.token) {
        updateUserLang({ lang }).catch(() => {});
      }
    },
    set_isLogin(state, isLogin) {
      state.isLogin = isLogin;
      localStorage.setItem('isLogin', isLogin);
    },
    set_userInfo(state, userInfo) {
      state.userInfo = userInfo;
      localStorage.setItem('userInfo', JSON.stringify(userInfo));
    },
    setToken(state, token) {
      state.token = token;
      localStorage.setItem('token', token);
    },
    changeVirtual(state, virtualStatus) {
      state.virtualStatus = virtualStatus;
      localStorage.setItem('virtualStatus', virtualStatus);
    },
    CHANGE_WS_MARLET(state, data) {
      state.marketWsList = data;
    },
    SET_ENABLED_MARKET_TYPES(state, types) {
      state.enabledMarketTypes = types;
    },
    INCREMENT_MARKET_DATA_VERSION(state) {
      state.marketDataVersion++;
    },
    SET_LAST_MARKET_TYPE(state, type) {
      state.lastMarketType = type;
    },
    UPDATE_REAL_TIME_PNL(state, pnlData) {
      // 🚀 最终修复：使用更大阈值避免微小波动导致的闪烁，确保只有显著变化才更新UI
      let significantChanges = {};
      let hasSignificantChanges = false;

      Object.keys(pnlData).forEach(orderId => {
        const newData = pnlData[orderId];
        const oldData = state.realTimePnLData[orderId];

        if (!oldData) {
          // 新订单数据，直接添加
          significantChanges[orderId] = {
            ...newData,
            lastUpdateTime: Date.now()
          };
          hasSignificantChanges = true;
        } else {
          // 使用更大的阈值，避免微小波动引起闪烁
          const profitChanged = Math.abs((oldData.unrealizedPnL || 0) - (newData.unrealizedPnL || 0)) >= 0.1; // 提高到0.1USD
          const rateChanged = Math.abs((oldData.unrealizedPnLPercentage || 0) - (newData.unrealizedPnLPercentage || 0)) >= 0.1; // 提高到0.1%
          const priceChanged = Math.abs((oldData.currentPrice || 0) - (newData.currentPrice || 0)) >= 0.01; // 提高到0.01

          if (profitChanged || rateChanged || priceChanged) {
            significantChanges[orderId] = {
              ...newData,
              lastUpdateTime: Date.now()
            };
            hasSignificantChanges = true;
          }
        }
      });

      if (hasSignificantChanges) {
        state.realTimePnLData = { ...state.realTimePnLData, ...significantChanges };
      } else {
      }
    },
    UPDATE_USER_BALANCE(state, balanceData) {
      state.userBalanceData = { ...state.userBalanceData, ...balanceData };
    },
    AI_PROFIT_UPDATE(state, data) {
      state.aiProfitData = { ...data, receivedAt: Date.now() };
    },
    SET_AI_ADMIN_DISABLED(state, val) {
      state.aiAdminDisabled = val;
    },
    SET_AI_RESTORED_NOTIFY(state, val) {
      state.aiRestoredNotify = val;
    },
    SET_AI_NOTIFY_DATA(state, { title, message }) {
      state.aiNotifyTitle = title;
      state.aiNotifyMessage = message;
    },
    ADD_NOTIFICATION(state, notification) {
      const item = { id: Date.now() + '_' + Math.random().toString(36).substr(2, 6), read: false, ...notification };
      state.notifications.unshift(item);
      if (state.notifications.length > 50) state.notifications = state.notifications.slice(0, 50);
      localStorage.setItem('notifications', JSON.stringify(state.notifications));
    },
    MARK_NOTIFICATIONS_READ(state) {
      state.notifications.forEach(n => n.read = true);
      localStorage.setItem('notifications', JSON.stringify(state.notifications));
    },
    CLEAR_NOTIFICATIONS(state) {
      state.notifications = [];
      localStorage.removeItem('notifications');
    },
    setContentsList(state, contentsList) {
      state.contentsList = contentsList;
    },
    setLangIndex(state, langIndex) {
      state.langIndex = langIndex;
    },
    SET_LANG_LIST(state, langList) {
      state.langList = langList;
    },
    setShowBoard(state, value) {
      state.showBoard = value;
      sessionStorage.setItem('showBoard', value);
    },
    // 🚀 新增：设置钱包绑定信息
    SET_BOUND_WALLET(state, { address, chainType }) {
      state.boundWalletAddress = address;
      state.boundChainType = chainType;
      localStorage.setItem('boundWalletAddress', address);
      localStorage.setItem('boundChainType', chainType);
    },
    // 🚀 新增：清除钱包绑定信息
    CLEAR_BOUND_WALLET(state) {
      state.boundWalletAddress = '';
      state.boundChainType = '';
      localStorage.removeItem('boundWalletAddress');
      localStorage.removeItem('boundChainType');
    },
    SET_VISITOR_MODE(state, val) {
      state.visitorMode = val;
    },
    SET_LANDING_CONFIG(state, { enabled, style, data }) {
      state.landingPageEnabled = enabled || '0';
      state.landingPageStyle = style || '1';
      state.landingPageData = data || null;
      state.landingConfigLoaded = true;
    },
    SET_SHOWING_LANDING(state, val) {
      state.showingLandingPage = !!val;
    },
    SET_LANDING_ENTERED(state, val) {
      state.landingEntered = !!val;
    },
    SET_HOME_TEMPLATE(state, tpl) {
      state.homeTemplate = tpl || 'default';
      localStorage.setItem('homeTemplate', state.homeTemplate);
    },
    SET_AI_LEVEL_DISPLAY_MODE(state, mode) {
      state.aiLevelDisplayMode = mode || 'grid';
      localStorage.setItem('aiLevelDisplayMode', state.aiLevelDisplayMode);
    },
    SET_THEME_MODE_CONFIG(state, { enabled, lightColors, darkColors }) {
      state.themeModeEnabled = enabled;
      state.themeLightColors = lightColors;
      state.themeDarkColors = darkColors;
    },
    TOGGLE_THEME_MODE(state) {
      const newMode = state.currentThemeMode === 'dark' ? 'light' : 'dark';
      state.currentThemeMode = newMode;
      localStorage.setItem('themeMode', newMode);
      const colors = newMode === 'light' ? state.themeLightColors : state.themeDarkColors;
      if (colors && Object.keys(colors).length > 0) {
        state.themeColors = colors;
        try { localStorage.setItem('cachedThemeColors', JSON.stringify(colors)); } catch(e) {}
        const root = document.documentElement;
        const cssVarMap = {
          theme_primary_color: '--color-primary',
          theme_primary_grad_start: '--color-primary-grad-start',
          theme_primary_grad_end: '--color-primary-grad-end',
          theme_bg_color: '--color-bg',
          theme_card_bg_color: '--color-card-bg',
          theme_nav_bg_color: '--color-nav-bg',
          theme_text_color: '--color-text',
          theme_text_secondary: '--color-text-secondary',
          theme_border_color: '--color-border',
          theme_up_color: '--color-up',
          theme_down_color: '--color-down',
          theme_success_color: '--color-success',
          theme_warning_color: '--color-warning',
          theme_danger_color: '--color-danger',
        };
        Object.keys(cssVarMap).forEach(key => {
          if (colors[key]) root.style.setProperty(cssVarMap[key], colors[key]);
        });
        const gs = colors.theme_primary_grad_start;
        const ge = colors.theme_primary_grad_end;
        if (gs && ge) root.style.setProperty('--color-primary-gradient', `linear-gradient(180deg, ${gs}, ${ge})`);
        const bgHex = (colors.theme_bg_color || '').replace('#', '');
        if (bgHex.length >= 6) {
          const r = parseInt(bgHex.substr(0, 2), 16);
          const g = parseInt(bgHex.substr(2, 2), 16);
          const b = parseInt(bgHex.substr(4, 2), 16);
          const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
          const isLight = luma > 128;
          root.style.setProperty('--icon-filter', isLight ? 'brightness(0)' : 'brightness(0) invert(1)');
          root.style.setProperty('--color-bg-hover', isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)');
          const parseHex = (h) => { h = (h || '').replace('#', ''); return [parseInt(h.substr(0,2),16)||0, parseInt(h.substr(2,2),16)||0, parseInt(h.substr(4,2),16)||0]; };
          const [sr,sg,sb] = parseHex(colors.theme_success_color);
          const [wr,wg,wb] = parseHex(colors.theme_warning_color);
          const [dr,dg,db] = parseHex(colors.theme_danger_color);
          root.style.setProperty('--color-success-alpha', `rgba(${sr},${sg},${sb},0.1)`);
          root.style.setProperty('--color-warning-alpha', `rgba(${wr},${wg},${wb},0.1)`);
          root.style.setProperty('--color-danger-alpha', `rgba(${dr},${dg},${db},0.1)`);
          const geHex = (colors.theme_primary_grad_end || '').replace('#', '');
          if (geHex.length >= 6) {
            const gr = parseInt(geHex.substr(0, 2), 16);
            const gg = parseInt(geHex.substr(2, 2), 16);
            const gb = parseInt(geHex.substr(4, 2), 16);
            const gLuma = 0.2126 * gr + 0.7152 * gg + 0.0722 * gb;
            root.style.setProperty('--color-btn-gradient-text', gLuma > 128 ? '#000' : '#fff');
          }
          const pHex = (colors.theme_primary_color || '').replace('#', '');
          if (pHex.length >= 6) {
            const pr = parseInt(pHex.substr(0, 2), 16);
            const pg = parseInt(pHex.substr(2, 2), 16);
            const pb = parseInt(pHex.substr(4, 2), 16);
            const pLuma = 0.2126 * pr + 0.7152 * pg + 0.0722 * pb;
            root.style.setProperty('--color-primary-contrast', pLuma > 128 ? '#000' : '#fff');
            root.style.setProperty('--color-primary-contrast-muted', pLuma > 128 ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)');
            root.style.setProperty('--color-primary-contrast-hover', pLuma > 128 ? 'rgba(0,0,0,0.16)' : 'rgba(255,255,255,0.16)');
          }
        }
      }
    },
    SET_THEME_COLORS(state, colors) {
      console.log('[ThemeColors] SET_THEME_COLORS called with:', colors);
      state.themeColors = colors;
      // Cache to localStorage for instant apply on next refresh
      try { localStorage.setItem('cachedThemeColors', JSON.stringify(colors)); } catch(e) {}
      const root = document.documentElement;
      const cssVarMap = {
        theme_primary_color: '--color-primary',
        theme_primary_grad_start: '--color-primary-grad-start',
        theme_primary_grad_end: '--color-primary-grad-end',
        theme_bg_color: '--color-bg',
        theme_card_bg_color: '--color-card-bg',
        theme_nav_bg_color: '--color-nav-bg',
        theme_text_color: '--color-text',
        theme_text_secondary: '--color-text-secondary',
        theme_border_color: '--color-border',
        theme_up_color: '--color-up',
        theme_down_color: '--color-down',
        theme_success_color: '--color-success',
        theme_warning_color: '--color-warning',
        theme_danger_color: '--color-danger',
      };
      Object.keys(cssVarMap).forEach(key => {
        if (colors[key]) {
          root.style.setProperty(cssVarMap[key], colors[key]);
        }
      });
      // Composite gradient variable
      const gs = colors.theme_primary_grad_start;
      const ge = colors.theme_primary_grad_end;
      if (gs && ge) {
        root.style.setProperty('--color-primary-gradient', `linear-gradient(180deg, ${gs}, ${ge})`);
      }
      // Derived variables based on background luminance
      const bgHex = (colors.theme_bg_color || '').replace('#', '');
      if (bgHex.length >= 6) {
        const r = parseInt(bgHex.substr(0, 2), 16);
        const g = parseInt(bgHex.substr(2, 2), 16);
        const b = parseInt(bgHex.substr(4, 2), 16);
        const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        const isLight = luma > 128;
        root.style.setProperty('--icon-filter', isLight ? 'brightness(0)' : 'brightness(0) invert(1)');
        root.style.setProperty('--color-bg-hover', isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)');
        const parseHex = (h) => { h = (h || '').replace('#', ''); return [parseInt(h.substr(0,2),16)||0, parseInt(h.substr(2,2),16)||0, parseInt(h.substr(4,2),16)||0]; };
        const [sr,sg,sb] = parseHex(colors.theme_success_color);
        const [wr,wg,wb] = parseHex(colors.theme_warning_color);
        const [dr,dg,db] = parseHex(colors.theme_danger_color);
        root.style.setProperty('--color-success-alpha', `rgba(${sr},${sg},${sb},0.1)`);
        root.style.setProperty('--color-warning-alpha', `rgba(${wr},${wg},${wb},0.1)`);
        root.style.setProperty('--color-danger-alpha', `rgba(${dr},${dg},${db},0.1)`);
        if (ge) {
          const geHex = ge.replace('#', '');
          if (geHex.length >= 6) {
            const gr2 = parseInt(geHex.substr(0, 2), 16);
            const gg2 = parseInt(geHex.substr(2, 2), 16);
            const gb2 = parseInt(geHex.substr(4, 2), 16);
            const gLuma = 0.2126 * gr2 + 0.7152 * gg2 + 0.0722 * gb2;
            root.style.setProperty('--color-btn-gradient-text', gLuma > 128 ? '#000' : '#fff');
          }
        }
        const pHex = (colors.theme_primary_color || '').replace('#', '');
        if (pHex.length >= 6) {
          const pr = parseInt(pHex.substr(0, 2), 16);
          const pg = parseInt(pHex.substr(2, 2), 16);
          const pb = parseInt(pHex.substr(4, 2), 16);
          const pLuma = 0.2126 * pr + 0.7152 * pg + 0.0722 * pb;
          root.style.setProperty('--color-primary-contrast', pLuma > 128 ? '#000' : '#fff');
          root.style.setProperty('--color-primary-contrast-muted', pLuma > 128 ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)');
          root.style.setProperty('--color-primary-contrast-hover', pLuma > 128 ? 'rgba(0,0,0,0.16)' : 'rgba(255,255,255,0.16)');
        }
      }
    },
    SET_BRAND(state, { siteName, siteLogo, siteLogoDark }) {
      if (siteName !== undefined) {
        state.siteName = siteName;
        localStorage.setItem('siteName', siteName);
        if (siteName) document.title = siteName;
      }
      if (siteLogo !== undefined) {
        state.siteLogo = siteLogo;
        localStorage.setItem('siteLogo', siteLogo);
      }
      if (siteLogoDark !== undefined) {
        state.siteLogoDark = siteLogoDark;
        localStorage.setItem('siteLogoDark', siteLogoDark);
      }
    },
    SET_MOBILE_NAV_MODE(state, mode) {
      state.mobileNavMode = mode;
      localStorage.setItem('mobileNavMode', mode);
    },
    SET_DISPLAY_TIMEZONE(state, tz) {
      state.displayTimezone = tz;
      localStorage.setItem('displayTimezone', tz);
      setDisplayTimezone(tz);
    },
    SET_CUSTOMER_SERVICE(state, { enabled, url, icon, agents }) {
      if (enabled !== undefined) {
        state.customerServiceEnabled = enabled;
        localStorage.setItem('customerServiceEnabled', enabled);
      }
      if (url !== undefined) {
        state.customerServiceUrl = url;
        localStorage.setItem('customerServiceUrl', url);
      }
      if (icon !== undefined) {
        state.customerServiceIcon = icon;
        localStorage.setItem('customerServiceIcon', icon);
      }
      if (agents !== undefined) {
        state.customerServiceAgents = agents;
        localStorage.setItem('customerServiceAgents', JSON.stringify(agents));
      }
    },
    SET_SALESMARTLY(state, { enabled, script }) {
      if (enabled !== undefined) {
        state.salesmartlyEnabled = enabled;
        localStorage.setItem('salesmartlyEnabled', enabled);
      }
      if (script !== undefined) {
        state.salesmartlyScript = script;
        localStorage.setItem('salesmartlyScript', script);
      }
    },
  },
  actions: {
    fetchLoanSwitch({ commit }) {
      return import('@/api/market').then(({ getParamByKey }) => {
        return getParamByKey('global_loan_enabled').then(res => {
          if (res.code == '200' && res.result) {
            commit('SET_GLOBAL_LOAN_ENABLED', res.result.paramValue || '1');
          }
        });
      }).catch(() => {});
    },
    fetchContractsSwitch({ commit }) {
      return import('@/api/market').then(({ getParamByKey }) => {
        return getParamByKey('global_contracts_enabled').then(res => {
          if (res.code == '200' && res.result) {
            commit('SET_GLOBAL_CONTRACTS_ENABLED', res.result.paramValue || '1');
          }
        });
      }).catch(() => {});
    },
    fetchLeverageSwitch({ commit }) {
      return import('@/api/market').then(({ getParamByKey }) => {
        return getParamByKey('global_leverage_enabled').then(res => {
          if (res.code == '200' && res.result) {
            commit('SET_GLOBAL_LEVERAGE_ENABLED', res.result.paramValue || '1');
          }
        });
      }).catch(() => {});
    },
    fetchAppDownloadShow({ commit }) {
      return import('@/api/market').then(({ getParamByKey }) => {
        return getParamByKey('app_download_show').then(res => {
          if (res.code == '200' && res.result) {
            commit('SET_APP_DOWNLOAD_SHOW', res.result.paramValue || '1');
          }
        });
      }).catch(() => {});
    },
    fetchAppDownloadUrls({ commit }) {
      return import('@/api/market').then(({ getParamByKey }) => {
        getParamByKey('app_download_ios_url').then(res => {
          if (res.code == '200' && res.result) {
            commit('SET_APP_DOWNLOAD_IOS_URL', res.result.paramValue || '');
          }
        }).catch(() => {});
        getParamByKey('app_download_android_url').then(res => {
          if (res.code == '200' && res.result) {
            commit('SET_APP_DOWNLOAD_ANDROID_URL', res.result.paramValue || '');
          }
        }).catch(() => {});
      }).catch(() => {});
    },
    fetchShowFloatingPnl({ commit }) {
      return import('@/api/market').then(({ getParamByKey }) => {
        return getParamByKey('show_floating_pnl').then(res => {
          if (res.code == '200' && res.result) {
            commit('SET_SHOW_FLOATING_PNL', res.result.paramValue || '1');
          }
        });
      }).catch(() => {});
    },
    fetchCustodySettings({ commit }) {
      return import('@/api/market').then(({ getParamByKey }) => {
        return Promise.all([
          getParamByKey('custody_account_enabled').then(res => {
            if (res.code == '200' && res.result) {
              commit('SET_CUSTODY_ACCOUNT_ENABLED', res.result.paramValue || '1');
            }
          }),
          getParamByKey('custody_account_percentage').then(res => {
            if (res.code == '200' && res.result) {
              commit('SET_CUSTODY_ACCOUNT_PERCENTAGE', res.result.paramValue || '10');
            }
          })
        ]);
      }).catch(() => {});
    },
    fetchVisibleMenus({ commit }) {
      return getVisibleMenus().then(res => {
        if (res && res.code == '200' && Array.isArray(res.result)) {
          commit('SET_VISIBLE_MENU_KEYS', res.result.map(m => m.menuKey));
        } else {
          commit('SET_VISIBLE_MENU_KEYS', null);
        }
      }).catch(() => {
        commit('SET_VISIBLE_MENU_KEYS', null);
      });
    },
    loadLanguages({ commit, state }) {
      return import('@/api/translation').then(({ getActiveLanguages }) => {
        return getActiveLanguages().then(res => {
          if (res.code == '200' && res.result && res.result.length) {
            const langList = res.result.map(l => ({ name: l.nativeName, value: l.langCode }))
            commit('SET_LANG_LIST', langList)
            const currentLang = state.currentLanguage || 'en'
            const idx = langList.findIndex(l => l.value === currentLang)
            commit('setLangIndex', idx >= 0 ? idx : 0)
          }
        })
      }).catch(() => { /* 失败时保留硬编码列表 */ })
    },
    fetchEnabledMarketTypes({ commit }) {
      return getEnabledMarketTypes().then(res => {
        if (res.code == '200' && res.result) {
          commit('SET_ENABLED_MARKET_TYPES', res.result);
        }
      }).catch(() => {});
    },
    fetchThemeColors({ commit, state }) {
      return import('@/api/market').then(({ getSettingsByType }) => {
        return getSettingsByType(11).then(res => {
          console.log('[ThemeColors] API response:', res);
          if (res.code == '200' && res.result) {
            const colors = {};
            let modeEnabled = false;
            let lightColors = null;
            let darkColors = null;
            res.result.forEach(item => {
              if (item.paramKey === 'theme_mode_enabled') {
                modeEnabled = item.paramValue === '1';
              } else if (item.paramKey === 'theme_light_colors' && item.paramValue && item.paramValue !== '{}') {
                try { lightColors = JSON.parse(item.paramValue).colors || null; } catch(e) {}
              } else if (item.paramKey === 'theme_dark_colors' && item.paramValue && item.paramValue !== '{}') {
                try { darkColors = JSON.parse(item.paramValue).colors || null; } catch(e) {}
              } else if (item.paramKey === 'home_template') {
                commit('SET_HOME_TEMPLATE', item.paramValue || 'default');
              } else if (item.paramKey === 'ai_level_display_mode') {
                commit('SET_AI_LEVEL_DISPLAY_MODE', item.paramValue || 'grid');
              } else if (item.paramKey && item.paramValue) {
                colors[item.paramKey] = item.paramValue;
              }
            });
            commit('SET_THEME_MODE_CONFIG', { enabled: modeEnabled, lightColors, darkColors });
            if (modeEnabled && lightColors && darkColors) {
              const userMode = state.currentThemeMode;
              const modeColors = userMode === 'light' ? lightColors : darkColors;
              commit('SET_THEME_COLORS', modeColors);
            } else {
              console.log('[ThemeColors] parsed colors:', colors);
              commit('SET_THEME_COLORS', colors);
            }
          }
        });
      }).catch(err => { console.error('[ThemeColors] fetch error:', err); });
    },
    fetchBrand({ commit, state }) {
      const lang = state.currentLanguage || 'en';
      return import('@/api/market').then(({ getParamByKey }) => {
        const fetchDefault = Promise.all([
          getParamByKey('site_name').then(res => (res.code == '200' && res.result != null) ? (res.result.paramValue || '') : null).catch(() => null),
          getParamByKey('site_logo').then(res => (res.code == '200' && res.result != null) ? (res.result.paramValue || '') : null).catch(() => null),
          getParamByKey('site_favicon').then(res => (res.code == '200' && res.result != null) ? (res.result.paramValue || '') : null).catch(() => null),
          getParamByKey('site_logo_dark').then(res => (res.code == '200' && res.result != null) ? (res.result.paramValue || '') : null).catch(() => null),
        ]);
        const fetchTranslation = import('@/config').then(({ VUE_APP_BASEURL }) => {
          return import('axios').then(({ default: axios }) => {
            return axios.get(VUE_APP_BASEURL + 'swx/entityTranslation/public/batch', {
              params: { entityType: 'settings', langCode: lang }
            }).then(res => {
              const list = (res.data && res.data.result) || [];
              const match = list.find(t => t.entityId === 'site_name' && t.fieldName === 'name');
              return match ? match.transValue : null;
            }).catch(() => null);
          });
        });
        return Promise.all([fetchDefault, fetchTranslation]).then(([[name, logo, favicon, logoDark], translatedName]) => {
          const finalName = translatedName || (name != null ? name : '');
          import('@/config').then(({ VUE_APP_BASEURL }) => {
            const base = VUE_APP_BASEURL.replace(/\/$/, '');
            const fullLogo = logo ? (logo.startsWith('http') ? logo : base + logo) : '';
            const fullLogoDark = logoDark ? (logoDark.startsWith('http') ? logoDark : base + logoDark) : '';
            commit('SET_BRAND', { siteName: finalName, siteLogo: fullLogo, siteLogoDark: fullLogoDark });
            if (favicon) {
              const faviconUrl = favicon.startsWith('http') ? favicon : (base + favicon);
              let link = document.querySelector("link[rel*='icon']");
              if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
              link.href = faviconUrl;
            }
          });
        });
      }).catch(() => {});
    },
    fetchCustomerService({ commit }) {
      return import('@/api/market').then(({ getParamByKey }) => {
        return Promise.all([
          getParamByKey('customer_service_enabled').then(res => (res.code == '200' && res.result != null) ? (res.result.paramValue || '0') : '0').catch(() => '0'),
          getParamByKey('customer_service_url').then(res => (res.code == '200' && res.result != null) ? (res.result.paramValue || '') : '').catch(() => ''),
          getParamByKey('customer_service_icon').then(res => (res.code == '200' && res.result != null) ? (res.result.paramValue || '') : '').catch(() => ''),
          getParamByKey('customer_service_agents').then(res => {
            if (res.code == '200' && res.result != null && res.result.paramValue) {
              try { return JSON.parse(res.result.paramValue); } catch(e) { return []; }
            }
            return [];
          }).catch(() => []),
          getParamByKey('salesmartly_enabled').then(res => (res.code == '200' && res.result != null) ? (res.result.paramValue || '0') : '0').catch(() => '0'),
          getParamByKey('salesmartly_script').then(res => (res.code == '200' && res.result != null) ? (res.result.paramValue || '') : '').catch(() => ''),
        ]).then(([enabled, url, icon, agents, smEnabled, smScript]) => {
          commit('SET_CUSTOMER_SERVICE', { enabled, url, icon, agents });
          commit('SET_SALESMARTLY', { enabled: smEnabled, script: smScript });
        });
      }).catch(() => {});
    },
    fetchMobileNavMode({ commit }) {
      return import('@/api/market').then(({ getParamByKey }) => {
        return getParamByKey('mobile_nav_mode').then(res => {
          if (res.code == '200' && res.result) {
            commit('SET_MOBILE_NAV_MODE', res.result.paramValue || 'tabbar');
          }
        });
      }).catch(() => {});
    },
    fetchDisplayTimezone({ commit }) {
      return import('@/api/market').then(({ getParamByKey }) => {
        return getParamByKey('display_timezone').then(res => {
          if (res.code == '200' && res.result) {
            commit('SET_DISPLAY_TIMEZONE', res.result.paramValue || 'Asia/Shanghai');
          }
        });
      }).catch(() => {});
    },
    fetchVisitorMode({ commit }) {
      return import('@/api/market').then(({ getParamByKey }) => {
        return getParamByKey('visitor_mode').then(res => {
          if (res.code == '200' && res.result) {
            commit('SET_VISITOR_MODE', res.result.paramValue || 'login');
          } else {
            commit('SET_VISITOR_MODE', 'login');
          }
        });
      }).catch(() => {
        commit('SET_VISITOR_MODE', 'login');
      });
    },
    fetchLandingConfig({ commit }) {
      return import('@/api/market').then(({ getParamByKey }) => {
        return Promise.all([
          getParamByKey('landing_page_enabled').then(res => (res.code == '200' && res.result != null) ? (res.result.paramValue || '0') : '0').catch(() => '0'),
          getParamByKey('landing_page_style').then(res => (res.code == '200' && res.result != null) ? (res.result.paramValue || '1') : '1').catch(() => '1'),
          getParamByKey('landing_page_data').then(res => (res.code == '200' && res.result != null) ? (res.result.paramValue || null) : null).catch(() => null),
        ]).then(([enabled, style, data]) => {
          commit('SET_LANDING_CONFIG', { enabled, style, data });
        });
      }).catch(() => {});
    },
    listContents({ commit, state }, lang) {
      return new Promise((resolve, reject) => {
        listContents(lang).then(res => {
          if (res.code == '200') {
            commit('setContentsList', res.result);
          }
          resolve(res);
        }).catch(err => {
          reject(err);
        })
      })
    },
    
    loginOut({ commit, state }) {
      return new Promise((resolve, reject) => {
        // 增强：清理所有本地存储
        removeToken();
        localStorage.removeItem('userInfo');
        localStorage.removeItem('isLogin');
        localStorage.removeItem('token');
        
        // 新增：清理钱包相关存储
        localStorage.removeItem('walletAddress');
        localStorage.removeItem('walletChain');
        
        // 🚀 新增：清理钱包绑定信息
        localStorage.removeItem('boundWalletAddress');
        localStorage.removeItem('boundChainType');
        
        // 提交状态变更
        commit('setToken', '');
        commit('set_isLogin', false);
        commit('set_userInfo', {});
        commit('CLEAR_BOUND_WALLET'); // 🚀 新增：清除钱包绑定
        
        // 新增：关闭WebSocket连接
        if (state.ws) {
          state.ws.close();
        }
        
        resolve();
      })
    },
    
    // 🚀 修改：增强登录验证
    login({ commit, state }, params) {
      return new Promise((resolve, reject) => {
        // 🚀 新增：钱包登录验证
        if (params.loginType === 'wallet') {
          // 验证钱包地址格式
          if (!params.walletAddress || !params.walletAddress.match(/^0x[a-fA-F0-9]{40}$|^T[A-Za-z1-9]{33}$/)) {
            reject(new Error('无效的钱包地址'));
            return;
          }
          
          // 验证签名（如果提供了签名）
          if (params.signature) {
            // 这里可以添加更复杂的签名验证逻辑
          }
        }

        const loginApi = params.loginType === 'wallet' ? loginByWallet : login;

        loginApi(params).then(res => {
          if (res.code == '200') {
            setToken(res.result.token);
            localStorage.setItem('userInfo', JSON.stringify(res.result));
            localStorage.setItem('isLogin', true);
            
            // 🚀 新增：保存钱包绑定信息
            if (params.loginType === 'wallet') {
              commit('SET_BOUND_WALLET', {
                address: params.walletAddress,
                chainType: params.chain
              });
            }
            
            commit('setToken', res.result.token);
            commit('set_isLogin', true);
            commit('set_userInfo', res.result);
          }
          resolve(res);
        }).catch(err => {
          reject(err);
        })
      })
    },
    
    // 🚀 新增：验证钱包连接状态
// 🚀 修改：增强验证钱包连接状态
// 🚀 修改：增强验证钱包连接状态
async verifyWalletConnection({ state, dispatch }) {
  return new Promise(async (resolve) => {
    const boundWalletAddress = state.boundWalletAddress;
    const boundChainType = state.boundChainType;
    
    if (!boundWalletAddress) {
      resolve(false);
      return;
    }
    
    try {
      // 🆕 修改：添加异步检查，等待钱包初始化
      let isConnected = false;
      
      if (boundChainType === 'erc') {
        // 对于以太坊钱包，可能需要等待钱包初始化
        isConnected = await checkEthereumConnectionWithRetry(boundWalletAddress);
      } else {
        isConnected = checkWeb3Connection(boundWalletAddress, boundChainType);
      }
      
      // 🚨 关键修复：移除自动登出逻辑，只返回验证结果
      if (!isConnected) {
        resolve(false);
      } else {
        resolve(true);
      }
    } catch (error) {
      console.error('验证钱包连接时出错:', error);
      resolve(false);
    }
  });
},
    GET_USER_INFO({ commit, state, dispatch }) {
  return new Promise((resolve, reject) => {
    getUserInfo().then(res => {
      if (res.code == '200') {
        
        // 🚀 恢复钱包验证逻辑，但不自动登出
        if (state.boundWalletAddress) {
          dispatch('verifyWalletConnection').then(isValid => {
            if (!isValid) {
              // 🟢 不reject，继续处理用户信息
            }
            
            // 无论钱包验证结果如何，都继续处理用户信息
            localStorage.setItem('userInfo', JSON.stringify(res.result));
            localStorage.setItem('isLogin', true);
            commit('set_isLogin', true);
            commit('set_userInfo', res.result);
            resolve(res);
          });
        } else {
          // 没有绑定钱包，正常处理
          localStorage.setItem('userInfo', JSON.stringify(res.result));
          localStorage.setItem('isLogin', true);
          commit('set_isLogin', true);
          commit('set_userInfo', res.result);
          resolve(res);
        }
      } else {
        resolve(res);
      }
    }).catch(err => {
      console.error('获取用户信息出错:', err);
      reject(err);
    })
  })
},
    
    GET_WS_MARKET({ commit, state, dispatch }) {
      try {
        return new Promise((resolve, reject) => {
          if (state.isLogin && state.boundWalletAddress) {
            dispatch('verifyWalletConnection').then(isValid => {
              if (!isValid) {
                reject(new Error('钱包连接已断开'));
                return;
              }
              establishWebSocketConnection(store, commit, state, resolve, reject);
            });
          } else {
            establishWebSocketConnection(store, commit, state, resolve, reject);
          }
        })
      } catch (error) {
        console.error('捕获到异常: ' + error.message);
      }
    }
  },
  modules: {
  }
})

// 立即执行：获取贷款开关状态
store.dispatch('fetchLoanSwitch');
store.dispatch('fetchContractsSwitch');
store.dispatch('fetchLeverageSwitch');
store.dispatch('fetchShowFloatingPnl');
store.dispatch('fetchAppDownloadShow');
store.dispatch('fetchAppDownloadUrls');

export default store

// 🚀 新增：检查Web3连接状态的辅助函数
// 🚀 修改：增强检查Web3连接状态的辅助函数
function checkWeb3Connection(boundWalletAddress, boundChainType) {
  try {
    if (boundChainType === 'erc') {
      // 🆕 增强：检查多种以太坊提供商
      let ethereumProvider = null;
      
      // 检测所有可能的以太坊提供商
      if (window.ethereum) {
        ethereumProvider = window.ethereum;
      } else if (window.onchain) {
        ethereumProvider = window.onchain;
      } else if (window.okxwallet) {
        ethereumProvider = window.okxwallet;
      }
      
      if (!ethereumProvider) {
        return false;
      }
      
      // 🆕 修改：更宽松的连接检查
      const isProviderConnected = ethereumProvider.isConnected ? 
        ethereumProvider.isConnected() : 
        (ethereumProvider.selectedAddress || ethereumProvider._state ? true : false);
      
      // 🆕 修改：检查当前选择的地址
      const currentAddress = ethereumProvider.selectedAddress;
      
      return isProviderConnected && 
             currentAddress && 
             currentAddress.toLowerCase() === boundWalletAddress.toLowerCase();
      
    } else if (boundChainType === 'tron') {
      // 检查TRON钱包（保持不变）
      if (window.tronWeb && window.tronWeb.ready) {
        return window.tronWeb.defaultAddress.base58 === boundWalletAddress;
      }
      if (window.okxwallet?.tronLink) {
        return window.okxwallet.tronLink.tronWeb.defaultAddress.base58 === boundWalletAddress;
      }
    }
    return false;
  } catch (error) {
    console.error('检查Web3连接状态失败:', error);
    return false;
  }
  // 🆕 新增：带重试的以太坊连接检查
async function checkEthereumConnectionWithRetry(boundWalletAddress, maxRetries = 3) {
  for (let i = 0; i < maxRetries; i++) {
    try {
      
      let ethereumProvider = null;
      
      // 检测所有可能的以太坊提供商
      if (window.ethereum) {
        ethereumProvider = window.ethereum;
      } else if (window.onchain) {
        ethereumProvider = window.onchain;
      } else if (window.okxwallet) {
        ethereumProvider = window.okxwallet;
      }
      
      if (!ethereumProvider) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        continue;
      }
      
      // 🆕 重要：对于Onchain，可能需要请求账户访问
      if (window.onchain && !ethereumProvider.selectedAddress) {
        try {
          const accounts = await ethereumProvider.request({ 
            method: 'eth_requestAccounts' 
          });
          if (accounts.length > 0) {
          }
        } catch (requestError) {
        }
      }
      
      // 检查连接状态
      const isProviderConnected = ethereumProvider.isConnected ? 
        ethereumProvider.isConnected() : 
        (ethereumProvider.selectedAddress || ethereumProvider._state ? true : false);
      
      const currentAddress = ethereumProvider.selectedAddress;
      
      if (isProviderConnected && currentAddress && 
          currentAddress.toLowerCase() === boundWalletAddress.toLowerCase()) {
        return true;
      }
      
      // 等待后重试
      await new Promise(resolve => setTimeout(resolve, 1000));
      
    } catch (error) {
      console.error(`第 ${i + 1} 次连接检查失败:`, error);
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }
  
  return false;
}
}